/**
 * Step 2 of the Upload Wizard — single-select channel grid.
 *
 * Renders all six platform channels (`Object.keys(CHANNELS)`), not just
 * `collection.channels`. Channels not yet on the collection get an
 * italicised "*new channel*" suffix; selecting one extends the
 * collection on submit.
 */

import React from 'react';
import { Check } from 'lucide-react';

import { cn } from '../../../../../lib/utils';
import { CHANNELS, type ChannelKey } from '../../shared/channels';
import { useChannelDatasets } from '../hooks/useDatasets';
import type { Collection } from '../types';

interface Props {
  collection: Collection;
  channelKey: ChannelKey | null;
  onChange: (k: ChannelKey | null) => void;
}

const Step2SelectChannel: React.FC<Props> = ({ collection, channelKey, onChange }) => {
  // Defensive `enabled` — Step2 is mounted only when the wizard is on
  // step 2 today, but explicitly gating here keeps parity with the tab
  // hooks (SplitTab / OverviewTab / CoverageTab / QualityTab) and
  // future-proofs the wizard against unconditional mounting.
  const { data: datasets, isLoading } = useChannelDatasets(collection.id, {
    enabled: !!collection.id,
  });
  const existingByChannel = new Map(
    (datasets ?? []).map((d) => [d.channelKey, d]),
  );
  const inCollection = new Set(collection.channels);
  const dimensionCount = collection.dimensions.length;

  return (
    <div className="space-y-4">
      <div
        className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3"
        data-testid="step2-grid"
      >
        {(Object.keys(CHANNELS) as ChannelKey[]).map((ch) => {
          const meta = CHANNELS[ch];
          const existing = existingByChannel.get(ch)?.stats.totalRecords ?? 0;
          const isNewChannel = !inCollection.has(ch);
          const isSelected = channelKey === ch;
          return (
            <button
              key={ch}
              type="button"
              onClick={() => onChange(isSelected ? null : ch)}
              aria-pressed={isSelected}
              data-testid={`step2-channel-${ch}`}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-md border p-3 text-left transition',
                isSelected
                  ? 'border-primary/40 bg-accent/40'
                  : 'border-border hover:bg-muted/30',
              )}
            >
              <span
                className="h-3 w-3 shrink-0 rounded-full"
                style={{ backgroundColor: meta.color }}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground">{meta.name}</div>
                <div className="text-[12px] text-muted-foreground">
                  {isLoading ? '—' : `${existing.toLocaleString()} existing records`}
                  {isNewChannel && !isLoading && (
                    <em data-testid={`step2-new-channel-${ch}`}> · new channel</em>
                  )}
                </div>
              </div>
              {isSelected && (
                <Check className="h-4 w-4 text-primary" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>

      {channelKey && (
        <p
          className="text-[13px] text-muted-foreground"
          data-testid="step2-confirmation"
        >
          {CHANNELS[channelKey].name} selected — records will be annotated across all{' '}
          {dimensionCount} dimensions
        </p>
      )}
    </div>
  );
};

export default Step2SelectChannel;
